/**
 * Client-side peer token store — IndexedDB-backed token management for
 * peer-to-peer route access.
 *
 * Provides CUID2-based indirection (tokens can be renewed in-place without
 * changing the reference ID), tagging for filtering, and lifecycle management
 * (revocation, expiry, auto-renewal).
 *
 * Analogous to localWebDAV/src/storedTokens.mjs but client-side.
 */

import { jose, localforage, createId } from '../deps.js'
import { canonicalizeScope, checkScopeAccess } from './chains.js'
import { watchStore } from '../acequia/storageWatchdog.js'

// Lazy import to avoid circular dependency (acequia.js → routes.js → routeAuth.js → peerTokens.js → acequia.js)
let _getUserKeys, _getUserId
async function getAcequiaIdentity() {
    if (!_getUserKeys) {
        const mod = await import('../acequia/acequia.js')
        _getUserKeys = mod.getUserKeys
        _getUserId = mod.getUserId
    }
    return { getUserKeys: _getUserKeys, getUserId: _getUserId }
}

// ---------------------------------------------------------------------------
// IndexedDB stores — separate stores for minted (I granted) vs granted (I received)
// prevents same-device apps from overwriting each other's records.
// ---------------------------------------------------------------------------

let mintedStore, grantedStore
if (typeof window !== 'undefined' || typeof self !== 'undefined') {
    try {
        mintedStore = watchStore(localforage.createInstance({ name: 'acequia-peer-tokens-minted' }), 'acequia-peer-tokens-minted')
        grantedStore = watchStore(localforage.createInstance({ name: 'acequia-peer-tokens-granted' }), 'acequia-peer-tokens-granted')
    } catch {
        // Non-browser context — stores will be null
    }
}

/** Get the store for a given origin. */
function storeFor(origin) {
    if (origin === 'minted') return mintedStore
    if (origin === 'granted') return grantedStore
    return mintedStore // default fallback
}

/** Find a record by id across both stores. Returns { store, record } or null. */
/**
 * A record by id. Without `origin`, the minted store first: a grantor's own
 * record wins when a holder in the same browser kept its copy under the same
 * id (the holders did, before acequia 3.0.45). A caller that knows which side
 * it is on names the store, so a holder's renewal never lands on the
 * grantor's record and a grantor's revocation never on a holder's copy.
 * @param {string} id
 * @param {'minted'|'granted'} [origin]
 */
async function findById(id, origin) {
    if (mintedStore && origin !== 'granted') {
        const r = await mintedStore.getItem(id)
        if (r) return { store: mintedStore, record: r }
    }
    if (grantedStore && origin !== 'minted') {
        const r = await grantedStore.getItem(id)
        if (r) return { store: grantedStore, record: r }
    }
    return null
}

// ---------------------------------------------------------------------------
// Migration from legacy single store
// ---------------------------------------------------------------------------

let _migrated = false
async function migrateFromLegacyStore() {
    if (_migrated) return
    _migrated = true
    if (!mintedStore || !grantedStore) return

    let legacyStore
    try {
        legacyStore = localforage.createInstance({ name: 'acequia-peer-tokens' })
    } catch { return }

    // Check if legacy store has data
    const count = await legacyStore.length()
    if (count === 0) return

    console.log(`[peerTokens] Migrating ${count} records from legacy store...`)
    await legacyStore.iterate(async (record, key) => {
        const store = storeFor(record.origin)
        if (store) {
            const existing = await store.getItem(key)
            if (!existing) await store.setItem(key, record)
        }
    })

    // Clear legacy store after migration
    await legacyStore.clear()
    console.log(`[peerTokens] Migration complete`)
}

// Run migration on module load (non-blocking)
if (mintedStore && grantedStore) {
    migrateFromLegacyStore().catch(e => console.warn('[peerTokens] migration error:', e))
}

// ---------------------------------------------------------------------------
// Change listeners
// ---------------------------------------------------------------------------

const changeListeners = []

function fireChange(event) {
    for (const cb of changeListeners) {
        try { cb(event) } catch (e) { console.error('[peerTokens] listener error:', e) }
    }
}

/**
 * Register a callback for token changes.
 * @param {function} callback - Called with { type, id, record? }
 *   type: 'created' | 'updated' | 'renewed' | 'revoked' | 'removed'
 * @returns {function} Unsubscribe function
 */
export function onTokenChange(callback) {
    changeListeners.push(callback)
    return () => {
        const idx = changeListeners.indexOf(callback)
        if (idx !== -1) changeListeners.splice(idx, 1)
    }
}

// ---------------------------------------------------------------------------
// Scopes are grant sets; names are healed at the boundary
// ---------------------------------------------------------------------------
//
// A token's scope is a grant set — { grants: [{ privileges, pattern }] } —
// never a name (unified-token-system, decision 6). A name in a token was a
// pointer into whoever verified it: the named-scope registry that resolved
// it is gone, and an unregistered name used to resolve to an empty scope, a
// deny by accident (the debug console's 'console-readonly', 2026-09-23).
//
// Tokens signed before this carry the names below. They are healed where
// they enter — a presented token's claim, a stored record's name, a wire
// message — into the grant set the name meant, and every heal logs the
// marker LEGACY_SCOPE_HEAL (decision 7). When the marker has been quiet for
// 30 days across the fleet, this table and the heal go. A name not in it is
// refused with a reason, never read as an empty scope.

const READ = ['read']
const READ_WRITE = ['bind', 'read', 'unbind', 'write-content', 'write-properties']

const LEGACY_SCOPE_NAMES = {
    full: { grants: [{ privileges: READ_WRITE, pattern: '*' }] },
    location: { grants: [{ privileges: READ, pattern: 'geo/*' }] },
    'console-readonly': { grants: [{ privileges: READ, pattern: 'console/logs' }] },
}

const _healsLogged = new Set()

/** Log a heal once per kind and detail in this page (the marker is grepped, not counted). */
function logHeal(where, detail) {
    const key = `${where}|${detail}`
    if (_healsLogged.has(key)) return
    _healsLogged.add(key)
    console.warn(`[peerTokens] LEGACY_SCOPE_HEAL ${where}: ${detail}`)
}

/**
 * A scope as a grant set: an object is returned as is (legacy
 * { paths, writePaths } included — readers canonicalize); a legacy name is
 * healed to the grant set it meant and logged; an unknown name is refused.
 * @param {string|object|null} scope
 * @param {string} [where] - the entry point, for the heal log
 * @returns {{ scope: object|null, healed?: string, reason?: string }}
 */
export function scopeGrantSet(scope, where = 'scope') {
    if (scope == null) return { scope: { grants: [] } }
    if (typeof scope === 'string') {
        const healed = LEGACY_SCOPE_NAMES[scope]
        if (!healed) return { scope: null, reason: `unknown scope name "${scope}"` }
        logHeal(where, `name "${scope}"`)
        return { scope: healed, healed: scope }
    }
    return { scope }
}

/** The grant set a verified token carries: its record's (local, trusted), else its claim's. */
function grantSetOf(record, claims) {
    if (record) {
        // A record stored from a name kept the name: heal it, since the
        // stored object may be the empty scope the old registry gave an
        // unregistered name
        if (record.scopeName && LEGACY_SCOPE_NAMES[record.scopeName]) {
            return scopeGrantSet(record.scopeName, 'stored record')
        }
        if (record.scope) return scopeGrantSet(record.scope, 'stored record')
    }
    return scopeGrantSet(claims?.scope, 'token claim')
}

/** Whether a token's audience admits the expected app; a token without one is healed. */
export function audienceAllows(claims, aud) {
    if (!aud) return { ok: true }
    const tokenAud = claims?.aud
    if (tokenAud == null) {
        logHeal('audience', `token without aud presented to "${aud}"`)
        return { ok: true }
    }
    const list = Array.isArray(tokenAud) ? tokenAud : [tokenAud]
    return list.includes(aud) ? { ok: true } : { ok: false, reason: `wrong audience: ${list.join(', ')} (expected ${aud})` }
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/**
 * Create a new peer token record.
 * @param {object} opts
 * @param {string} opts.jwt - JWT string
 * @param {string} [opts.name] - Human-readable label
 * @param {string} [opts.note] - Description
 * @param {object|string} [opts.scope] - A grant set ({ grants }). A legacy name ('full',
 *   'location', 'console-readonly') is healed to its grant set and logged; any other
 *   string is kept only as the record's label (a holder's copy of a wire label, e.g.
 *   'view') and stored without a scope
 * @param {string[]} [opts.tags] - Filterable labels
 * @param {'minted'|'granted'} opts.origin - Did we create or receive this token?
 * @param {string} [opts.peerId] - instanceId of the peer
 * @param {string} [opts.deviceId] - Stable deviceId of the peer
 * @param {string} [opts.groupName] - Acequia group
 * @param {string} [opts.expiresAt] - ISO timestamp or null
 * @param {boolean} [opts.autoRenew] - Auto-renew before expiry?
 * @param {number} [opts.ttlSeconds] - Original TTL for renewal
 * @param {string} [opts.granteeHandle] - Human-readable handle of the grantee
 * @param {string} [opts.granteeUserId] - Stable userId of the grantee
 * @param {string} [opts.id] - Use this CUID2 instead of generating a new one (for unified granter/grantee IDs)
 * @returns {Promise<string>} The CUID2 id
 */
export async function createPeerToken(opts) {
    const origin = opts.origin || 'granted'
    const store = storeFor(origin)
    if (!store) throw new Error('Peer token store not available')

    const id = opts.id || createId()
    const now = new Date().toISOString()

    // The scope is stored as a canonical grant set
    const set = scopeGrantSet(opts.scope, 'createPeerToken')
    let scope = set.scope
    if (scope) {
        scope = canonicalizeScope(scope)
    } else {
        logHeal('createPeerToken', `${set.reason} — stored as a label, without a scope`)
    }

    // Build tags array with automatic tags
    const tags = [...(opts.tags || [])]
    if (origin && !tags.includes(origin)) tags.push(origin)
    if (opts.autoRenew && !tags.includes('auto-renew')) tags.push('auto-renew')

    const record = {
        id,
        jwt: opts.jwt,
        name: opts.name || null,
        note: opts.note || null,
        // A name the record was made from: a label, read until the sunset
        scopeName: typeof opts.scope === 'string' ? opts.scope : null,
        scope,
        tags,
        origin,

        peerId: opts.peerId || null,
        deviceId: opts.deviceId || null,
        groupName: opts.groupName || null,

        // Grantee identity (optional — populated when granting to a known user)
        granteeHandle: opts.granteeHandle || null,
        granteeUserId: opts.granteeUserId || null,
        // For a grantee that isn't signed in (no handle): its browser's device
        // name, and the app it used (CameraMap, a remote viewer…)
        granteeDeviceName: opts.granteeDeviceName || null,
        granteeApp: opts.granteeApp || null,

        createdAt: now,
        expiresAt: opts.expiresAt || null,
        lastUsed: null,
        status: 'active',
        revokedAt: null,

        autoRenew: opts.autoRenew || false,
        ttlSeconds: opts.ttlSeconds || null,
        renewedAt: null,
    }

    // Remove old tokens for the same deviceId + tags + group in this store to prevent duplicates
    // Tags represent the app context (e.g. ['geocamera','full'] vs ['debug-console','console-readonly']),
    // so tokens from different apps for the same device coexist safely; so do one app's grants
    // to a device in two groups (each group is its own grant).
    if (record.deviceId) {
        const newTags = record.tags || []
        const tagsMatch = (a, b) => a.length === b.length && a.every((t, i) => t === b[i])
        const toRemove = []
        await store.iterate((existing, key) => {
            if (key !== id && existing.deviceId === record.deviceId
                && (existing.groupName || null) === (record.groupName || null)
                && tagsMatch(existing.tags || [], newTags)) {
                toRemove.push(key)
            }
        })
        for (const key of toRemove) {
            await store.removeItem(key)
            fireChange({ type: 'removed', id: key, record: null })
        }
    }

    await store.setItem(id, record)
    fireChange({ type: 'created', id, record })
    return id
}

/**
 * Get a peer token record by CUID.
 * @param {string} id
 * @returns {Promise<object|null>}
 */
export async function getPeerToken(id) {
    const found = await findById(id)
    if (!found) return null
    updateExpiredStatus(found.record)
    return found.record
}

/**
 * Get just the JWT for a CUID — the hot path for auth resolution.
 * Returns null if not found, revoked, or expired (without auto-renew).
 * @param {string} id
 * @returns {Promise<string|null>}
 */
export async function getJwtById(id) {
    const found = await findById(id)
    if (!found) return null
    const { store, record } = found
    if (record.status === 'revoked') return null
    if (isExpired(record) && !record.autoRenew) return null

    // Update lastUsed (fire-and-forget)
    record.lastUsed = new Date().toISOString()
    store.setItem(id, record)

    return record.jwt
}

/**
 * Update fields on a peer token record.
 * @param {string} id
 * @param {object} updates - Fields to merge
 * @returns {Promise<object>} Updated record
 */
export async function updatePeerToken(id, updates, { origin } = {}) {
    const found = await findById(id, origin)
    if (!found) throw new Error(`Peer token not found: ${id}`)

    const updated = { ...found.record, ...updates }
    await found.store.setItem(id, updated)
    fireChange({ type: 'updated', id, record: updated })
    return updated
}

/**
 * Remove a peer token record permanently.
 * @param {string} id
 */
export async function removePeerToken(id, { origin } = {}) {
    const found = await findById(id, origin)
    if (!found) return
    await found.store.removeItem(id)
    // The store it left: a cache of the other store keeps its own record of that id
    fireChange({ type: 'removed', id, origin: found.record.origin })
}

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

/**
 * List peer tokens with optional filters.
 * @param {object} [filters]
 * @param {string[]} [filters.tags] - All listed tags must be present
 * @param {'minted'|'granted'} [filters.origin]
 * @param {string} [filters.groupName]
 * @param {'active'|'expired'|'revoked'} [filters.status]
 * @param {string} [filters.deviceId]
 * @returns {Promise<object[]>}
 */
export async function listPeerTokens(filters = {}) {
    const results = []

    // Determine which stores to iterate
    const stores = []
    if (!filters.origin || filters.origin === 'minted') { if (mintedStore) stores.push(mintedStore) }
    if (!filters.origin || filters.origin === 'granted') { if (grantedStore) stores.push(grantedStore) }
    if (stores.length === 0) return []

    for (const store of stores) {
        await store.iterate((record) => {
            updateExpiredStatus(record)

            if (filters.origin && record.origin !== filters.origin) return
            if (filters.groupName && record.groupName !== filters.groupName) return
            if (filters.status && record.status !== filters.status) return
            if (filters.deviceId && record.deviceId !== filters.deviceId) return
            if (filters.tags) {
                for (const tag of filters.tags) {
                    if (!record.tags?.includes(tag)) return
                }
            }
            results.push(record)
        })
    }

    results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    return results
}

/**
 * Find the best active token for a specific peer in a group.
 * Returns the token with the latest expiry.
 * @param {string} deviceId
 * @param {string} groupName
 * @returns {Promise<object|null>}
 */
export async function findTokenForPeer(deviceId, groupName) {
    const tokens = await listPeerTokens({
        deviceId,
        groupName,
        status: 'active',
    })

    if (tokens.length === 0) return null

    // Prefer latest expiry (most time remaining)
    tokens.sort((a, b) => {
        if (!a.expiresAt) return -1  // never-expiring first
        if (!b.expiresAt) return 1
        return new Date(b.expiresAt) - new Date(a.expiresAt)
    })

    return tokens[0]
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Revoke a peer token. Keeps in store for display/audit.
 * @param {string} id
 * @returns {Promise<object>} Updated record
 */
export async function revokePeerToken(id, { origin } = {}) {
    const found = await findById(id, origin)
    if (!found) throw new Error(`Peer token not found: ${id}`)

    const updated = {
        ...found.record,
        status: 'revoked',
        revokedAt: new Date().toISOString(),
    }

    // Add revoked tag, remove active-related tags
    if (!updated.tags.includes('revoked')) updated.tags.push('revoked')
    updated.tags = updated.tags.filter(t => t !== 'expired')

    await found.store.setItem(id, updated)
    fireChange({ type: 'revoked', id, record: updated })
    return updated
}

/**
 * Renew a peer token in-place — updates JWT and expiry, keeps CUID stable.
 * This is the key operation for CUID indirection: clients hold the id,
 * the JWT behind it changes.
 * @param {string} id
 * @param {string} newJwt
 * @param {string} newExpiresAt - ISO timestamp
 * @returns {Promise<object>} Updated record
 */
export async function renewPeerToken(id, newJwt, newExpiresAt, { origin } = {}) {
    const found = await findById(id, origin)
    if (!found) throw new Error(`Peer token not found: ${id}`)
    const record = found.record

    const updated = {
        ...record,
        jwt: newJwt,
        expiresAt: newExpiresAt,
        renewedAt: new Date().toISOString(),
        status: 'active',
    }

    // Remove expired tag on renewal
    updated.tags = updated.tags.filter(t => t !== 'expired')

    await found.store.setItem(id, updated)
    fireChange({ type: 'renewed', id, record: updated })
    return updated
}

/**
 * Clean up old expired/revoked records.
 * @param {object} [opts]
 * @param {number} [opts.maxAge=2592000000] - Max age in ms for revoked/expired records (default 30 days)
 * @returns {Promise<number>} Number of records purged
 */
export async function cleanupExpired({ maxAge = 30 * 24 * 60 * 60 * 1000 } = {}) {
    if (!mintedStore && !grantedStore) return 0

    const cutoff = Date.now() - maxAge
    let removed = 0

    for (const store of [mintedStore, grantedStore]) {
        if (!store) continue
        const toRemove = []
        await store.iterate((record, key) => {
            if (record.status === 'revoked' && record.revokedAt && new Date(record.revokedAt).getTime() < cutoff) {
                toRemove.push(key)
            } else if (isExpired(record) && !record.autoRenew && record.expiresAt && new Date(record.expiresAt).getTime() < cutoff) {
                toRemove.push(key)
            }
        })
        for (const key of toRemove) {
            await store.removeItem(key)
        }
        removed += toRemove.length
    }

    return removed
}

// ---------------------------------------------------------------------------
// Public key cache — persistent (IndexedDB) + in-memory for hot path.
// Keys are immutable (kid = JWK thumbprint), so cache entries never go stale.
// ---------------------------------------------------------------------------

let keyStore
if (typeof window !== 'undefined' || typeof self !== 'undefined') {
    try {
        keyStore = localforage.createInstance({ name: 'acequia-public-keys' })
    } catch {
        // Non-browser context
    }
}

// In-memory cache: kid → { publicKey: CryptoKey, userId, status }
const keyCache = new Map()

/**
 * Resolve a public key by kid and userId.
 * Priority: in-memory → IndexedDB → local user keys → server fetch.
 * @param {string} kid - JWK thumbprint
 * @param {string} userId - Token subject (signer)
 * @returns {Promise<CryptoKey|null>}
 */
async function resolvePublicKey(kid, userId) {
    // 1. In-memory cache
    const cached = keyCache.get(kid)
    if (cached?.publicKey) return cached.publicKey

    // 2. IndexedDB persistent cache
    if (keyStore) {
        const stored = await keyStore.getItem(kid)
        if (stored?.jwk) {
            try {
                const publicKey = await jose.importJWK(stored.jwk, 'PS256')
                keyCache.set(kid, { publicKey, userId: stored.userId, status: stored.status })
                return publicKey
            } catch {
                // Corrupted entry — remove and fall through
                await keyStore.removeItem(kid)
            }
        }
    }

    // 3. Local user keys (self-issued tokens — no network needed)
    try {
        // This browser's user, now or before a device link (identities.js)
        const { ownIdentityFor } = await import('./identities.js')
        const own = await ownIdentityFor(userId)
        if (own) {
            const userKeys = own.userKeys
            if (userKeys?.publicKey) {
                const jwk = await jose.exportJWK(userKeys.publicKey)
                const localKid = await jose.calculateJwkThumbprint(jwk)
                if (localKid === kid) {
                    keyCache.set(kid, { publicKey: userKeys.publicKey, userId, status: 'active' })
                    if (keyStore) {
                        keyStore.setItem(kid, { jwk, userId, status: 'active', cachedAt: new Date().toISOString() })
                    }
                    return userKeys.publicKey
                }
            }
        }
    } catch {
        // getUserId/getUserKeys may not be ready yet — fall through to network
    }

    // 4. Fetch from auth server
    try {
        const origin = (typeof location !== 'undefined') ? location.origin : null
        if (!origin) return null

        const resp = await fetch(`${origin}/auth/users/${userId}/keys/${kid}/public`)
        if (!resp.ok) return null

        const data = await resp.json()
        if (!data.success || !data.publicKey) return null
        if (data.status === 'revoked') return null

        const publicKey = await jose.importJWK(data.publicKey, 'PS256')
        keyCache.set(kid, { publicKey, userId, status: data.status })
        if (keyStore) {
            keyStore.setItem(kid, { jwk: data.publicKey, userId, status: data.status, cachedAt: new Date().toISOString() })
        }
        return publicKey
    } catch {
        return null
    }
}

// ---------------------------------------------------------------------------
// Verification helpers — for data channel handlers and route auth
// ---------------------------------------------------------------------------

/**
 * Verify a peer token with cryptographic signature check.
 * Resolves the signer's public key (cached locally, fetched on miss),
 * verifies the JWT signature, then checks expiry and revocation.
 *
 * Verification is mandatory: a token that cannot be verified is invalid.
 * There is no "probably fine" result — an unresolvable signer, a missing
 * `kid`/`sub`, a missing `exp`, or a `typ: "anchor"` header all return
 * `valid: false`. Callers may read `keyUnresolved` to distinguish "could not
 * verify" from "verified and rejected", but neither is grounds for access.
 *
 * Then the token's audience and scope:
 * - `aud`: when the caller names one, a token for another app is refused
 *   before its scope is read (unified-token-system, 2026-09-23). A token
 *   minted before audiences existed passes, logged LEGACY_SCOPE_HEAL.
 * - `scope` on the result is the grant set the token carries: its record's
 *   when it was presented by id, else its claim's. A legacy name is healed
 *   (logged); an unknown name is a rejection.
 *
 * @param {string} jwtOrCuid - JWT string or CUID2 id
 * @param {object} [opts]
 * @param {string} [opts.aud] - the app this token must be for ('geocamera', 'debug-console')
 * @returns {Promise<{ valid: boolean, claims?: object, record?: object, scope?: object, reason?: string, keyUnresolved?: boolean }>}
 */
export async function verifyPeerToken(jwtOrCuid, { aud } = {}) {
    // Check if it's a CUID (no dots) or a JWT
    let jwt = jwtOrCuid
    let record = null

    if (!jwtOrCuid.includes('.')) {
        // CUID — resolve to JWT
        record = await getPeerToken(jwtOrCuid)
        if (!record) return { valid: false, reason: 'token not found' }
        if (record.status === 'revoked') return { valid: false, reason: 'revoked' }
        if (isExpired(record) && !record.autoRenew) return { valid: false, reason: 'expired' }
        jwt = record.jwt
    }

    // Decode JWT header and payload
    let claims, header
    try {
        claims = jose.decodeJwt(jwt)
        header = jose.decodeProtectedHeader(jwt)
    } catch {
        return { valid: false, reason: 'malformed JWT' }
    }

    // Guard 1 — an anchor never authenticates. Anchors are inert structural
    // nodes: exp-less by design and fetchable by anyone. The server refuses
    // them at every entry point (ANCHOR_NOT_PRESENTABLE); this is the client
    // counterpart of that invariant.
    if (header.typ === 'anchor') {
        return { valid: false, reason: 'anchor is not presentable', claims }
    }

    // Guard 2 — signature verification is mandatory.
    //
    // Previously this whole block was conditional on `kid && userId`, and an
    // unresolvable key returned `valid: true` with an advisory `keyUnresolved`
    // flag that no caller ever read. Two ways through, both ending with
    // routeAuth taking the token's scope from its own unverified claims:
    // omit `kid` and signature checking was skipped entirely, or present a kid
    // nobody can resolve and be admitted on the strength of saying so.
    const kid = header.kid || claims.kid
    const userId = claims.sub
    if (!kid || !userId) {
        return { valid: false, reason: 'unsigned token: missing kid or sub', claims }
    }

    const publicKey = await resolvePublicKey(kid, userId)
    if (!publicKey) {
        // Offline or unknown signer. Being unable to resolve the key is not a
        // reason to trust the claim — the answer is local-first resolution
        // (peer-held anchors, local key resolver), not admission. `keyUnresolved`
        // is kept on the rejection so callers can tell "cannot verify" from
        // "verified and bad".
        return { valid: false, reason: 'signer key unresolvable', claims, keyUnresolved: true }
    }

    try {
        // exp is enforced below (autoRenew tolerance), so skip jose's own check
        await jose.jwtVerify(jwt, publicKey, { currentDate: new Date(0) })
    } catch (e) {
        return { valid: false, reason: `signature invalid: ${e.message}`, claims }
    }

    // Guard 3 — a working `exp` is required. The old `if (claims.exp && ...)`
    // enforced expiry only when the claim happened to be present, so a token
    // minted without one never expired.
    if (typeof claims.exp !== 'number') {
        return { valid: false, reason: 'missing exp', claims }
    }
    if (claims.exp < Math.floor(Date.now() / 1000)) {
        // A locally-held record marked autoRenew is still allowed through — it
        // will be renewed. Tolerating a lapsed expiry is a separate question
        // from whether the token carries an expiry at all.
        if (!record?.autoRenew) {
            return { valid: false, reason: 'expired', claims }
        }
    }

    // Guard 4 — the audience, before the scope is read
    const audience = audienceAllows(claims, aud)
    if (!audience.ok) return { valid: false, reason: audience.reason, claims, record }

    // Guard 5 — a scope that can be read. An unknown name is a refusal with
    // a reason, never an empty scope
    const set = grantSetOf(record, claims)
    if (!set.scope) return { valid: false, reason: set.reason, claims, record }

    return { valid: true, claims, record, scope: set.scope }
}

/**
 * Check if a JWT or CUID authorizes access to a given scope.
 * @param {string} jwtOrCuid
 * @param {string} routePath - The route being accessed (without instanceId prefix)
 * @param {string} [method='GET'] - HTTP method
 * @param {object} [opts]
 * @param {string} [opts.aud] - the app the token must be for
 * @returns {Promise<{ allowed: boolean, reason?: string, claims?: object }>}
 */
export async function checkPeerAccess(jwtOrCuid, routePath, method = 'GET', { aud } = {}) {
    const verification = await verifyPeerToken(jwtOrCuid, { aud })
    if (!verification.valid) {
        return { allowed: false, reason: verification.reason }
    }

    // Privilege-aware, as chain tokens are checked: PROPPATCH, LOCK and the
    // rest map through the one method table (the read/write split by method
    // name this replaces didn't know them)
    const result = checkScopeAccess(method.toUpperCase(), routePath, verification.scope)
    if (!result.allowed) {
        return { allowed: false, reason: 'scope mismatch', claims: verification.claims }
    }

    return { allowed: true, claims: verification.claims }
}

// ---------------------------------------------------------------------------
// Auto-renewal engine
// ---------------------------------------------------------------------------

let renewalTimer = null

/**
 * Start the auto-renewal engine for minted tokens.
 * @param {object} opts
 * @param {number} [opts.renewalThreshold=0.1] - Renew at this fraction of TTL remaining
 * @param {number} [opts.minRemaining=300000] - Or this many ms remaining, whichever triggers first
 * @param {number} [opts.checkInterval=60000] - Check interval in ms
 * @param {function} opts.mintFn - (record) => Promise<{ jwt, expiresAt }> — app provides minting logic
 */
export function startAutoRenewal({
    renewalThreshold = 0.1,
    minRemaining = 5 * 60 * 1000,
    checkInterval = 60 * 1000,
    mintFn,
}) {
    if (!mintFn) throw new Error('mintFn is required for auto-renewal')
    stopAutoRenewal()

    renewalTimer = setInterval(async () => {
        try {
            await _runRenewalCheck({ renewalThreshold, minRemaining, mintFn })
        } catch (e) {
            console.error('[peerTokens] renewal check error:', e)
        }
    }, checkInterval)
}

/**
 * Stop the auto-renewal engine.
 */
export function stopAutoRenewal() {
    if (renewalTimer) {
        clearInterval(renewalTimer)
        renewalTimer = null
    }
}

async function _runRenewalCheck({ renewalThreshold, minRemaining, mintFn }) {
    const tokens = await listPeerTokens({
        origin: 'minted',
        status: 'active',
    })

    for (const record of tokens) {
        if (!record.autoRenew || !record.expiresAt) continue

        const expiresAt = new Date(record.expiresAt).getTime()
        const remaining = expiresAt - Date.now()
        if (remaining <= 0) continue // already expired, too late

        const ttlMs = (record.ttlSeconds || 3600) * 1000
        const thresholdMs = Math.max(ttlMs * renewalThreshold, minRemaining)

        if (remaining < thresholdMs) {
            try {
                const result = await mintFn(record)
                await renewPeerToken(record.id, result.jwt, result.expiresAt)
                // Change event already fired by renewPeerToken — listeners can push to peer
            } catch (e) {
                console.warn(`[peerTokens] renewal failed for ${record.id}:`, e.message)
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Sync cache — in-memory mirror keyed by deviceId for hot-path reads
// ---------------------------------------------------------------------------

/**
 * Sync Map mirror of peerTokens, keyed by deviceId.
 * Hydrates once from IndexedDB, then stays current via onTokenChange.
 * All reads are synchronous O(1) — no IndexedDB on the hot path.
 *
 * @example
 *   const cache = new PeerTokenCache({ origin: 'minted', groupName: 'geocamera' })
 *   await cache.hydrate()
 *   const record = cache.get(deviceId) // sync
 */
export class PeerTokenCache {
    constructor(filters = {}) {
        this._map = new Map()       // deviceId → record
        this._idIndex = new Map()   // CUID id → deviceId (for removal events)
        this._filters = filters
        this._unsub = null
    }

    /**
     * Initial load from IndexedDB + subscribe to changes.
     * Must be called once before reading.
     */
    async hydrate() {
        const tokens = await listPeerTokens(this._filters)
        this._map.clear()
        this._idIndex.clear()
        for (const t of tokens) {
            if (t.deviceId) {
                const existing = this._map.get(t.deviceId)
                if (existing) {
                    // Keep the better token: usable > not, then latest expiry
                    if (!betterToken(t, existing)) continue
                    this._idIndex.delete(existing.id)
                }
                this._map.set(t.deviceId, t)
                this._idIndex.set(t.id, t.deviceId)
            }
        }

        // Subscribe to changes (replace existing subscription)
        if (this._unsub) this._unsub()
        this._unsub = onTokenChange((event) => this._handleChange(event))
    }

    _handleChange(event) {
        const { type, id, record } = event

        // An event about a record of the OTHER store that shares this id (a
        // holder's copy stored under the grantor's id, before acequia 3.0.45,
        // when both live in one browser) is not about the record held here
        const heldDeviceId = this._idIndex.get(id)
        const held = heldDeviceId ? this._map.get(heldDeviceId) : null
        const eventOrigin = record?.origin || event.origin || null
        if (held && eventOrigin && held.origin && held.origin !== eventOrigin) return

        if (type === 'removed') {
            // No record on removal — use id index. Another token held for
            // the device takes its place, rather than the device having none
            if (heldDeviceId) {
                this._map.delete(heldDeviceId)
                this._idIndex.delete(id)
                this._refill(heldDeviceId)
            }
            return
        }

        if (!record?.deviceId) return
        if (!this._matchesFilters(record)) {
            // Record no longer matches filters (e.g. revoked) — remove if present,
            // and fall back to the device's next-best token
            if (this._idIndex.has(id)) {
                this._map.delete(record.deviceId)
                this._idIndex.delete(id)
                this._refill(record.deviceId)
            }
            return
        }

        // A change to another of the device's tokens (an older one's lastUsed,
        // say) replaces the held one only when it's better: any change used to
        // win, so a worse token touched last became the one presented
        const current = this._map.get(record.deviceId)
        if (current && current.id !== id && !betterToken(record, current)) return
        if (current && current.id !== id) this._idIndex.delete(current.id)
        this._map.set(record.deviceId, record)
        this._idIndex.set(id, record.deviceId)
        // The held one changed for the worse (expired, say): another may be better
        if (current?.id === id && !usableToken(record)) this._refill(record.deviceId)
    }

    /**
     * The device's best token from the store, in place of a held one that
     * went (removed, revoked) or went bad. Only replaces what is held when
     * better: a change that landed meanwhile wins.
     */
    async _refill(deviceId) {
        let tokens
        try {
            tokens = await listPeerTokens({ ...this._filters, deviceId })
        } catch {
            return
        }
        let best = null
        for (const t of tokens) if (betterToken(t, best)) best = t
        if (!best || !this._unsub) return
        const current = this._map.get(deviceId)
        if (current && (current.id === best.id || !betterToken(best, current))) return
        if (current) this._idIndex.delete(current.id)
        this._map.set(deviceId, best)
        this._idIndex.set(best.id, deviceId)
    }

    _matchesFilters(record) {
        const f = this._filters
        if (f.origin && record.origin !== f.origin) return false
        if (f.groupName && record.groupName !== f.groupName) return false
        if (f.status && record.status !== f.status) return false
        if (f.deviceId && record.deviceId !== f.deviceId) return false
        if (f.tags) {
            for (const tag of f.tags) {
                if (!record.tags?.includes(tag)) return false
            }
        }
        return true
    }

    /** Get token record by deviceId (sync). */
    get(deviceId) { return this._map.get(deviceId) }

    /** Check if a deviceId has a token (sync). */
    has(deviceId) { return this._map.has(deviceId) }

    /** Number of cached tokens. */
    get size() { return this._map.size }

    /** Iterate all cached records. */
    values() { return this._map.values() }

    /** Iterate [deviceId, record] pairs. */
    entries() { return this._map.entries() }

    /** Find record by CUID token id (sync). */
    getById(id) {
        const deviceId = this._idIndex.get(id)
        return deviceId ? this._map.get(deviceId) : undefined
    }

    /** Iterable. */
    [Symbol.iterator]() { return this._map.entries() }

    /** Stop listening and clear cache. */
    destroy() {
        if (this._unsub) {
            this._unsub()
            this._unsub = null
        }
        this._map.clear()
        this._idIndex.clear()
    }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isExpired(record) {
    if (!record.expiresAt) return false
    return new Date(record.expiresAt).getTime() < Date.now()
}

/** A token that can be presented: active, and unexpired or renewed automatically. */
function usableToken(t) {
    return t.status === 'active' && (!isExpired(t) || !!t.autoRenew)
}

/** Whether `a` is a better token to hold than `b` (none): usable first, then the later expiry. */
function betterToken(a, b) {
    if (!b) return true
    if (usableToken(a) !== usableToken(b)) return usableToken(a)
    const exp = (t) => (t.expiresAt ? new Date(t.expiresAt).getTime() : Infinity)
    return exp(a) > exp(b)
}

/** Lazily update expired status on read */
function updateExpiredStatus(record) {
    if (record.status === 'active' && isExpired(record) && !record.autoRenew) {
        record.status = 'expired'
        if (!record.tags.includes('expired')) record.tags.push('expired')
        // Write back lazily (fire-and-forget)
        const s = storeFor(record.origin)
        if (s) s.setItem(record.id, record)
    }
}
